import { Link } from "react-router-dom";
import { ArrowUpRight, Clock3, Tag } from "lucide-react";
import Seo from "@/components/Seo";
import { MaskLines, Reveal, SectionHead } from "@/components/Reveal";
import { INSIGHTS } from "@/data/insights";

const fmt = (d) => new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

const InsightsIndex = () => {
  const [lead, ...rest] = INSIGHTS;
  return (
    <div data-testid="insights-page">
      <Seo
        title="Security Insights Wales | News & Advice | Xtreme Protection"
        description="Welsh security news and expert advice: SIA licensing, retail crime, construction site theft, event security, vacant property rules and rural crime across Wales."
        path="/insights"
      />
      <section className="border-b border-[#1C2027] bg-[#0D1015] pb-16 pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="eyebrow flex items-center gap-3">
            <span className="h-px w-10 bg-[#F5A800]" />
            The Briefing Room // Welsh Security Intelligence
          </p>
          <MaskLines className="font-condensed mt-6 text-5xl font-bold uppercase leading-[0.9] tracking-tight text-white sm:text-7xl" lines={["Security Insights", "For Wales"]} />
          <Reveal delay={0.3}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#9BA1AB] sm:text-lg">
              News, analysis and practical advice from the Welsh security front line — retail crime, construction theft, licensing, events and rural protection, written by people who work the ground.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <Reveal>
          <Link to={`/insights/${lead.slug}`} data-testid={`insight-lead-${lead.slug}`} className="group grid overflow-hidden border border-[#1C2027] bg-[#10131A] transition-colors duration-300 hover:border-[#F5A800]/50 lg:grid-cols-2">
            <div className="relative h-64 overflow-hidden lg:h-full">
              <img src={lead.image} alt={lead.title} className="h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#10131A] to-transparent lg:bg-gradient-to-r" />
            </div>
            <div className="flex flex-col justify-center p-8 lg:p-12">
              <div className="flex items-center gap-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50">
                <span className="flex items-center gap-1.5 text-[#F5A800]"><Tag className="h-3 w-3" />{lead.category}</span>
                <span>{fmt(lead.date)}</span>
                <span className="flex items-center gap-1.5"><Clock3 className="h-3 w-3" />{lead.read} min read</span>
              </div>
              <h2 className="font-condensed mt-4 text-3xl font-bold uppercase leading-[0.95] tracking-tight text-white transition-colors duration-300 group-hover:text-[#F5A800] sm:text-4xl">{lead.title}</h2>
              <p className="mt-4 text-sm leading-relaxed text-[#9BA1AB] sm:text-base">{lead.excerpt}</p>
              <span className="mt-6 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#F5A800]">
                Read the briefing <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </div>
          </Link>
        </Reveal>

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="insights-grid">
          {rest.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 3) * 0.07} y={18} className="h-full">
              <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group flex h-full flex-col border border-[#1C2027] bg-[#10131A] transition-colors duration-300 hover:border-[#F5A800]/50">
                <div className="relative h-44 overflow-hidden">
                  <img src={a.image} alt={a.title} className="h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#10131A] to-transparent" />
                  <span className="absolute left-4 top-4 bg-[#0A0C10]/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#F5A800]">{a.category}</span>
                </div>
                <div className="flex flex-1 flex-col justify-between p-6">
                  <div>
                    <h3 className="font-condensed text-xl font-bold uppercase leading-tight tracking-wide text-white transition-colors duration-300 group-hover:text-[#F5A800]">{a.title}</h3>
                    <p className="mt-3 text-[13px] leading-relaxed text-[#9BA1AB]">{a.excerpt}</p>
                  </div>
                  <div className="mt-5 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-white/40">
                    <span>{fmt(a.date)}</span>
                    <span className="flex items-center gap-1.5"><Clock3 className="h-3 w-3" />{a.read} min</span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default InsightsIndex;
